import {
  percentualParaTaxa,
  type ItemTributoResponse,
  type NovoItemTributoDto,
  type NovoItemTributoFormEntrada,
  type PerfilTributarioResponse,
  type TabelaPrecoResponse,
  type VigenciaResponse,
} from '@triade/contracts';
import { formatarTaxa, fracaoParaPercentualTexto } from '@/lib/format';

/**
 * Sessao 51 (U2): aba Tributacao do item no modo ESTRUTURADO da tabela padrao. Esta camada so decide QUAL modo vale e
 * CONVERTE o que foi digitado (percentual -> fracao, vazio = usa a tabela). Nenhuma aliquota e calculada aqui: os valores
 * da tabela vem prontos da API (perfil tributario).
 */

export type TributoTabela = 'PIS' | 'COFINS' | 'ICMS' | 'ISS' | 'IPI';

/** Campos de excecao do item (vazio = usa a tabela; "0" e um valor). */
export type CampoExcecao = 'excPis' | 'excCofins' | 'excIcms' | 'excIss' | 'excIpi';

export const CAMPOS_TAXA_EXCECAO: ReadonlyArray<{ campo: CampoExcecao; rotulo: TributoTabela }> = [
  { campo: 'excPis', rotulo: 'PIS' },
  { campo: 'excCofins', rotulo: 'COFINS' },
  { campo: 'excIcms', rotulo: 'ICMS' },
  { campo: 'excIss', rotulo: 'ISS' },
  { campo: 'excIpi', rotulo: 'IPI' },
];

const TRIBUTO_DO_CAMPO: Record<CampoExcecao, TributoTabela> = {
  excPis: 'PIS',
  excCofins: 'COFINS',
  excIcms: 'ICMS',
  excIss: 'ISS',
  excIpi: 'IPI',
};

/** Campos extras do formulario "nova vigencia" no modo estruturado (textos como digitados; vazio = nao informado). */
export interface CamposExcecaoForm {
  excPis: string;
  excCofins: string;
  excIcms: string;
  excIss: string;
  excIpi: string;
  importadoIcms4: boolean;
  reducaoBaseIcms: string;
  stMva: string;
  stAliquota: string;
  naturezaPresuncao: '' | 'BEM' | 'SERVICO';
  pisCofinsRegime: '' | 'NORMAL' | 'MONOFASICO' | 'ALIQUOTA_ZERO';
}

/** Formulario completo da vigencia: o do contrato (taxas legadas em %) mais os campos de excecao. */
export type FormVigencia = NovoItemTributoFormEntrada & CamposExcecaoForm;

/** Valores vazios de excecao (nada digitado = usa a tabela). */
export const CAMPOS_EXCECAO_VAZIOS: CamposExcecaoForm = {
  excPis: '',
  excCofins: '',
  excIcms: '',
  excIss: '',
  excIpi: '',
  importadoIcms4: false,
  reducaoBaseIcms: '',
  stMva: '',
  stAliquota: '',
  naturezaPresuncao: '',
  pisCofinsRegime: '',
};

export function valoresExcecao(v: ItemTributoResponse | null | undefined): CamposExcecaoForm {
  return {
    excPis: fracaoParaPercentualTexto(v?.excPis),
    excCofins: fracaoParaPercentualTexto(v?.excCofins),
    excIcms: fracaoParaPercentualTexto(v?.excIcms),
    excIss: fracaoParaPercentualTexto(v?.excIss),
    excIpi: fracaoParaPercentualTexto(v?.excIpi),
    importadoIcms4: v?.importadoIcms4 ?? false,
    reducaoBaseIcms: fracaoParaPercentualTexto(v?.reducaoBaseIcms),
    stMva: fracaoParaPercentualTexto(v?.stMva),
    stAliquota: fracaoParaPercentualTexto(v?.stAliquota),
    naturezaPresuncao: v?.naturezaPresuncao ?? '',
    pisCofinsRegime: v?.pisCofinsRegime ?? '',
  };
}

/** Modo da aba: CARREGANDO (ainda nao sabemos a tabela), LEGADO (tela atual) ou ESTRUTURADO. */
export interface ModoTabelaDoItem {
  modo: 'CARREGANDO' | 'LEGADO' | 'ESTRUTURADO';
  /** Perfil da vigencia (so vem com custo.ver; sem ele a tela mostra "usa a tabela" sem o valor). */
  perfil: PerfilTributarioResponse | null;
  tabelaNome: string | null;
}

export const MODO_LEGADO: ModoTabelaDoItem = { modo: 'LEGADO', perfil: null, tabelaNome: null };
const MODO_CARREGANDO: ModoTabelaDoItem = { modo: 'CARREGANDO', perfil: null, tabelaNome: null };

/** Tabela padrao da empresa (isDefault). */
export function tabelaPadrao(tabelas: TabelaPrecoResponse[] | undefined): TabelaPrecoResponse | undefined {
  return tabelas?.find((t) => t.isDefault);
}

/** Vigencia vigente na data: validFrom <= data e (validTo nulo ou posterior a data). ISO de data compara como texto. */
export function vigenciaVigente(vigencias: VigenciaResponse[] | undefined, hoje: string): VigenciaResponse | undefined {
  return vigencias?.find((v) => v.validFrom <= hoje && (!v.validTo || v.validTo > hoje));
}

/**
 * Decide o modo da aba pela tabela padrao e sua vigencia vigente. Sem tabela padrao, sem vigencia vigente, vigencia LEGADO
 * ou erro ao carregar: tela legada (como antes). Enquanto carrega: CARREGANDO (a tela nao grava antes de saber o modo).
 */
export function modoDaTabela(a: {
  lista: TabelaPrecoResponse[] | undefined;
  listaErro: boolean;
  detalhe: TabelaPrecoResponse | undefined;
  detalheErro: boolean;
  hoje: string;
}): ModoTabelaDoItem {
  if (a.listaErro) return MODO_LEGADO;
  if (!a.lista) return MODO_CARREGANDO;
  const padrao = tabelaPadrao(a.lista);
  if (!padrao) return MODO_LEGADO;
  if (a.detalheErro) return MODO_LEGADO;
  if (!a.detalhe) return MODO_CARREGANDO;
  const vigente = vigenciaVigente(a.detalhe.vigencias, a.hoje);
  if (vigente?.modo !== 'ESTRUTURADO') return MODO_LEGADO;
  return { modo: 'ESTRUTURADO', perfil: vigente.perfilTributario ?? null, tabelaNome: padrao.nome };
}

/** Aliquota da tabela para o tributo (linha ativa do perfil). Nulo = a tabela nao tem linha. */
export function valorDaTabela(perfil: PerfilTributarioResponse | null, tributo: TributoTabela): string | null {
  const linha = perfil?.tributos.find((t) => t.tributo === tributo && t.ativo);
  return linha ? linha.aliquota : null;
}

/**
 * Texto do valor da tabela para o tributo: a taxa formatada (formatador unico) ou o rotulo do caso sem linha
 * (PIS/COFINS = padrao do regime). Nulo quando nao ha perfil para mostrar (sem custo.ver).
 */
export function textoValorTabela(tributo: TributoTabela, perfil: PerfilTributarioResponse | null): string | null {
  if (perfil === null) return null;
  const valor = valorDaTabela(perfil, tributo);
  if (valor !== null) return formatarTaxa(valor);
  if (tributo === 'PIS' || tributo === 'COFINS') return 'padrão do regime';
  if (tributo === 'ICMS') return 'tabela legal da UF';
  return 'sem alíquota na tabela';
}

/** "usa a tabela (1,65%)" (ou "usa a tabela" sem perfil para mostrar). */
export function textoUsaTabela(tributo: TributoTabela, perfil: PerfilTributarioResponse | null): string {
  const tabela = textoValorTabela(tributo, perfil);
  return tabela === null ? 'usa a tabela' : `usa a tabela (${tabela})`;
}

/** Ajuda do campo de excecao: "usa a tabela (1,65%)" quando vazio; "exceção" + valor da tabela quando preenchido. */
export function ajudaExcecao(campo: CampoExcecao, digitado: string, perfil: PerfilTributarioResponse | null): { excecao: boolean; texto: string } {
  const tributo = TRIBUTO_DO_CAMPO[campo];
  if (digitado.trim() !== '') {
    const tabela = textoValorTabela(tributo, perfil);
    return { excecao: true, texto: tabela === null ? '' : `tabela: ${tabela}` };
  }
  return { excecao: false, texto: textoUsaTabela(tributo, perfil) };
}

const MSG_PERCENTUAL = 'Percentual invalido (0 a 100, ate 6 casas)';

export type CampoPercentualExcecao = CampoExcecao | 'reducaoBaseIcms' | 'stMva' | 'stAliquota';

export type ExcecaoCorpo = Pick<
  NovoItemTributoDto,
  'excPis' | 'excCofins' | 'excIcms' | 'excIss' | 'excIpi' | 'importadoIcms4' | 'reducaoBaseIcms' | 'stMva' | 'stAliquota' | 'naturezaPresuncao' | 'pisCofinsRegime'
>;

export type ResultadoExcecao = { ok: true; corpo: ExcecaoCorpo } | { ok: false; erros: Partial<Record<CampoPercentualExcecao, string>> };

/**
 * Converte os campos de excecao digitados para o corpo do POST (taxas em fracao; vazio = null = usa a tabela).
 * So desloca o ponto decimal (percentualParaTaxa, do contrato); percentual invalido vira erro no campo.
 */
export function excecaoParaCorpo(v: CamposExcecaoForm): ResultadoExcecao {
  const erros: Partial<Record<CampoPercentualExcecao, string>> = {};
  const converter = (campo: CampoPercentualExcecao): string | null => {
    const texto = v[campo].trim();
    if (texto === '') return null;
    const taxa = percentualParaTaxa(texto);
    if (taxa === null) erros[campo] = MSG_PERCENTUAL;
    return taxa;
  };
  const corpo: ExcecaoCorpo = {
    excPis: converter('excPis'),
    excCofins: converter('excCofins'),
    excIcms: converter('excIcms'),
    excIss: converter('excIss'),
    excIpi: converter('excIpi'),
    importadoIcms4: v.importadoIcms4,
    reducaoBaseIcms: converter('reducaoBaseIcms'),
    stMva: converter('stMva'),
    stAliquota: converter('stAliquota'),
    naturezaPresuncao: v.naturezaPresuncao === '' ? null : v.naturezaPresuncao,
    pisCofinsRegime: v.pisCofinsRegime === '' ? null : v.pisCofinsRegime,
  };
  return Object.keys(erros).length > 0 ? { ok: false, erros } : { ok: true, corpo };
}

/**
 * Legado do item no modo estruturado: as colunas pis..ipi continuam gravadas, espelhando a excecao (ou 0 quando usa a tabela),
 * para que o historico nao fique com um valor antigo que a API ja ignora.
 */
export function espelhoLegado(corpo: ExcecaoCorpo): Pick<NovoItemTributoDto, 'pis' | 'cofins' | 'icms' | 'iss' | 'ipi'> {
  return {
    pis: corpo.excPis ?? '0',
    cofins: corpo.excCofins ?? '0',
    icms: corpo.excIcms ?? '0',
    iss: corpo.excIss ?? '0',
    ipi: corpo.excIpi ?? '0',
  };
}
